// ─────────────────────────────────────────────────────────────
// Faffle quiz — question bank (v3, June 2026)
//
// To edit a question's wording: change the `prompt` or any `text`.
// Don't change the `letter` values — they map answers to traits:
//   A = Overthinker  B = Analyst  C = Avoider
//   D = People Pleaser  E = Chaotic Wildcard
// Answer order is shuffled at runtime, so the order here doesn't matter visually.
//
// Voice rule per trait (so answers stay in their own lane):
//   A Overthinker  = the loop in the head. Replay, rehearse, spiral, "you KNEW it."
//                    Never about NOT doing the thing (that's the Avoider).
//   B Analyst      = assess, calculate, compare, optimise. Data before action.
//   C Avoider      = ignore, defer, zone out, dodge. Passive. "Later (you won't)."
//   D People Pleaser = apologise, others first, won't make it awkward, silent self-sacrifice.
//   E Chaotic Wildcard = impulsive, confident, random, genuinely unbothered.
// ─────────────────────────────────────────────────────────────

window.FAFFLE_QUIZ_QUESTIONS = [
  // ── Group 1: Inside Your Head ───────────────────────────────
  {
    group: 1,
    id: "brain-bedtime",
    prompt: "You're lying in bed, finally still, and your brain decides now is the perfect time to:",
    options: [
      { letter: "A", text: "Replay that slightly awkward thing you said yesterday, then quietly draft three better ways you could have said it" },
      { letter: "B", text: "Calculate exactly how many hours of sleep you'll get if you drop off this second. Then recalculate." },
      { letter: "C", text: "Notice the thought. Decide it's a tomorrow thought. Fall asleep on principle." },
      { letter: "D", text: "Remember you said yes to three favours this week, none of which you wanted to do, all of which you will absolutely do" },
      { letter: "E", text: "Google whether you can technically own a wombat. Wide awake now. Worth it." }
    ]
  },
  {
    group: 1,
    id: "restaurant-pick",
    prompt: "You need to pick a restaurant for dinner with friends this weekend.",
    options: [
      { letter: "A", text: "Decided on Tuesday. Still lying awake rehearsing your order and whether everyone secretly hates the choice." },
      { letter: "B", text: "Spreadsheet open: menus, reviews, parking, dietary needs, all cross-checked before you commit" },
      { letter: "C", text: "Say you'll sort it. By Friday they've messaged for details and you've done nothing. Urgh." },
      { letter: "D", text: "Text everyone to check what suits them, then book the one place that keeps the whole group happy" },
      { letter: "E", text: "Book the first place with a fun name. Forget which one. Confidently lead everyone to the wrong door." }
    ]
  },
  {
    group: 1,
    id: "forgot-birthday",
    prompt: "You wake up and realise you forgot a friend's birthday yesterday.",
    options: [
      { letter: "A", text: "Spiral. You KNEW it was coming. You thought about it last week. How does your brain DO this to you?!" },
      { letter: "B", text: "Open your calendar to work out exactly how it slipped through, so it never happens again" },
      { letter: "C", text: "Decide it's basically fine. You'll say it in person when you see them next. If you remember." },
      { letter: "D", text: "Plan to make it up to them with a gift, a long lunch, and a homemade cake. The cake is non-negotiable now." },
      { letter: "E", text: "Fire off ‘HAPPY BELATED!!!’ with no apology, full confidence, and a photo of your breakfast" }
    ]
  },
  {
    group: 1,
    id: "shower-wander",
    prompt: "You're in the shower, and you end up:",
    options: [
      { letter: "A", text: "Rehearsing a conversation about something that upset you. You'll never actually have it. You just need to know exactly what you'd say." },
      { letter: "B", text: "Solving a problem you didn't know was bothering you until the hot water hit" },
      { letter: "C", text: "Zoned out so completely you can't remember if you've washed your hair. Wash it again. Maybe." },
      { letter: "D", text: "Mentally rearranging your week to help a friend through their thing, before they've even asked" },
      { letter: "E", text: "Inventing a business empire you'll have completely forgotten by the time you're dry" }
    ]
  },
  {
    group: 1,
    id: "friend-move",
    prompt: "A friend asks you to help them move house this weekend.",
    options: [
      { letter: "A", text: "Haven't replied yet, but you're already running every disaster. What if it rains. What if the couch won't fit again." },
      { letter: "B", text: "Already weighing it up. Which day, how many hours, whether they've hired a van or you ARE the van." },
      { letter: "C", text: "I would never. Too much trauma." },
      { letter: "D", text: "Say yes on the spot, even though your back's been wrecked all week. You will not mention the back. Ever." },
      { letter: "E", text: "Say yes, turn up with snacks, a playlist, and zero practical skills. Somehow the best day." }
    ]
  },

  // ── Group 2: Communication ──────────────────────────────────
  {
    group: 2,
    id: "text-unread",
    prompt: "You sent a message a while ago. It's been read. Still nothing back.",
    options: [
      { letter: "A", text: "Re-read your own message like it's evidence in a trial. Was that emoji friendly, or unhinged." },
      { letter: "B", text: "Work out when they were last online and the odds they're just slow, not dodging you" },
      { letter: "C", text: "Put the phone face down and decide you're not going to think about it. (You will.)" },
      { letter: "D", text: "Fire off a breezy follow-up letting them off the hook, in case your message felt like pressure" },
      { letter: "E", text: "Send a second, totally unrelated message like the first one never happened" }
    ]
  },
  {
    group: 2,
    id: "friend-advice",
    prompt: "A friend messages ‘what should I do?’ about a situation you know nothing about.",
    options: [
      { letter: "A", text: "Immediately assume you've missed something huge, and reread the whole chat for the clue that isn't there" },
      { letter: "B", text: "Refuse to weigh in until you've asked three clarifying questions and gathered the facts" },
      { letter: "C", text: "Send a quick ‘ugh, that's so hard’ and hope it covers it, because you do not have the energy for this today" },
      { letter: "D", text: "Send something warm and supportive instantly, despite having no idea what's going on" },
      { letter: "E", text: "Reply with bold, decisive advice based on absolutely zero information" }
    ]
  },
  {
    group: 2,
    id: "message-send",
    prompt: "You're about to message someone you've recently met to ask if they want to grab a coffee.",
    options: [
      { letter: "A", text: "Rewrite the invite nine times, then screenshot it to a friend for a second opinion" },
      { letter: "B", text: "Read it once for tone, casual but not too keen, fix one word, send" },
      { letter: "C", text: "Save it to drafts. You'll send it later. (You will not send it later.)" },
      { letter: "D", text: "Add ‘only if you're free, no pressure at all!’ so they've got an easy way out of every sentence" },
      { letter: "E", text: "Send it, immediately decide it sounded weird, send a ‘haha ignore me’ that's worse" }
    ]
  },
  {
    group: 2,
    id: "we-need-talk",
    prompt: "A close friend sends a message that just says ‘we need to talk.’",
    options: [
      { letter: "A", text: "Mentally review every interaction you've had with them since 2019" },
      { letter: "B", text: "Reply ‘about what?’ because you can't respond properly without more data" },
      { letter: "C", text: "Pretend you haven't seen it until you've emotionally prepared. Could be a while." },
      { letter: "D", text: "Reply ‘of course, anything you need’ and quietly start clearing your whole weekend for them" },
      { letter: "E", text: "Reply ‘ooh, drama’ with genuinely zero concern" }
    ]
  },
  {
    group: 2,
    id: "voice-note-replay",
    prompt: "A friend texts you a question. You reply with a voice note, then immediately listen back to your own voice.",
    options: [
      { letter: "A", text: "Why did you say it like THAT. Record another. That one's worse. Just send a text." },
      { letter: "B", text: "Assess the pacing, the clarity, and whether you actually answered their question" },
      { letter: "C", text: "Delete it. Type ‘lol yeah’ instead. Done." },
      { letter: "D", text: "Worry it sounded too blunt and send a softer follow-up walking half of it back" },
      { letter: "E", text: "Send it. Send a second one twice as long. Congratulations, they have a podcast now." }
    ]
  },
  {
    group: 2,
    id: "wrong-recipient",
    prompt: "You realise you've just sent a message to completely the wrong person.",
    options: [
      { letter: "A", text: "Stare at the screen in full-body horror, replaying the exact moment you tapped send" },
      { letter: "B", text: "Assess the damage, then draft the cleanest possible recovery message" },
      { letter: "C", text: "Pretend it never happened and quietly pray they never open it" },
      { letter: "D", text: "Fire off seventeen apologies in a row" },
      { letter: "E", text: "Double down. Send a follow-up like it was always meant for them. You're friends now." }
    ]
  },

  // ── Group 3: Social ─────────────────────────────────────────
  {
    group: 3,
    id: "group-chat",
    prompt: "You've been added to a group chat and it will not stop pinging.",
    options: [
      { letter: "A", text: "Read every message twice, convinced one of them is subtly about you. (None of them are. Probably.)" },
      { letter: "B", text: "Skim the lot, mentally summarise the key decisions, archive the noise" },
      { letter: "C", text: "Mute it. You'll catch up later. (You will never catch up.)" },
      { letter: "D", text: "Reply to everyone individually so nobody feels ignored" },
      { letter: "E", text: "Drop one completely unhinged message, then vanish without explanation" }
    ]
  },
  {
    group: 3,
    id: "rain-check",
    prompt: "You're dressed and halfway out the door when your friend texts ‘rain check?’",
    options: [
      { letter: "A", text: "Spiral about whether they actually wanted to see you, reread the text for clues, then reread it again. It's two words." },
      { letter: "B", text: "Instantly recalculate the night. What to eat, what to watch, new plan locked in." },
      { letter: "C", text: "Back in pyjamas before you've finished reading it. Secretly thrilled." },
      { letter: "D", text: "Reply ‘honestly, no worries at all!’, then send three new dates that might work better" },
      { letter: "E", text: "Text back ‘fine, I'm coming to YOURS’ and show up anyway" }
    ]
  },
  {
    group: 3,
    id: "uninvited-photo",
    prompt: "A friend posts a photo from a hangout you weren't invited to.",
    options: [
      { letter: "A", text: "Study every photo. Who's there. When was this. What does it mean. What did you do." },
      { letter: "B", text: "Weigh the evidence. Casual last-minute thing, or a deliberate leave-out?" },
      { letter: "C", text: "Keep scrolling. Didn't see it. (Saw it. Will think about it at 3am.)" },
      { letter: "D", text: "Like it immediately so absolutely no one suspects you're hurt" },
      { letter: "E", text: "Comment ‘wow, RUDE’ and then genuinely never think about it again" }
    ]
  },
  {
    group: 3,
    id: "room-quiet",
    prompt: "You walk into a room and everyone goes quiet.",
    options: [
      { letter: "A", text: "Assume they were talking about you and start scanning faces for proof" },
      { letter: "B", text: "Read the room. Body language, who's avoiding eye contact, context calculated." },
      { letter: "C", text: "Keep walking like you noticed nothing. Helps that you mostly didn't." },
      { letter: "D", text: "Smile, say hi, and spend the rest of the day being extra nice to everyone, just in case" },
      { letter: "E", text: "Announce ‘don't stop on my account’ and sit down like you own the place" }
    ]
  },
  {
    group: 3,
    id: "energy-drop",
    prompt: "You're mid-conversation and your friend's energy suddenly drops.",
    options: [
      { letter: "A", text: "Rewind the last three things you said. It was the second one. It was definitely the second one." },
      { letter: "B", text: "Quietly weigh it up. Something you said, something they're carrying, or something else entirely?" },
      { letter: "C", text: "Don't clock it at all. Keep talking. The story isn't finished." },
      { letter: "D", text: "Switch instantly to a topic you know will cheer them up, your own thread abandoned" },
      { letter: "E", text: "Say ‘what just happened to your face?’ and somehow make them laugh" }
    ]
  },

  // ── Group 4: Tasks & Productivity ───────────────────────────
  {
    group: 4,
    id: "dishwasher",
    prompt: "You walk into the kitchen and find the dishwasher loaded but never switched on.",
    options: [
      { letter: "A", text: "Stand there certain it was you who forgot to press start. It's always you. Is it always you?" },
      { letter: "B", text: "Check whether the dishes need a re-rinse or you can just run it as-is" },
      { letter: "C", text: "Shut the door. Walk away. A problem for whoever opens it next." },
      { letter: "D", text: "Switch it on, silently fuming, but say nothing because it's not worth a whole conversation" },
      { letter: "E", text: "Run it. Lob in something that absolutely should not go in a dishwasher. Walk away." }
    ]
  },
  {
    group: 4,
    id: "laundry",
    prompt: "The clean, dry washing has been sitting in the basket, ready to put away, for two days.",
    options: [
      { letter: "A", text: "Every time you walk past, your brain files a fresh complaint. You've thought about those clothes more than you've worn them." },
      { letter: "B", text: "Hold off until you can fold it in front of something good. Efficient use of an episode." },
      { letter: "C", text: "Just take what you need from the basket each morning. That IS the system." },
      { letter: "D", text: "Fold it the moment someone else mentions it's bugging them. Not a second before." },
      { letter: "E", text: "Fold one sock. Lose interest. Wear something straight off the top of the pile." }
    ]
  },
  {
    group: 4,
    id: "cafe-late",
    prompt: "You're running 15 minutes late to meet a friend at a café.",
    options: [
      { letter: "A", text: "Draft the apology in your head, then keep refining it the whole way there. It's just 'sorry, running late.'" },
      { letter: "B", text: "Share your live location so they can watch your ETA tick down in real time" },
      { letter: "C", text: "Decide they're probably running late too. It cancels out. Slow down slightly." },
      { letter: "D", text: "Stop on the way for their coffee and a little treat, so you turn up bearing gifts instead of excuses" },
      { letter: "E", text: "Stroll in late holding a coffee from a DIFFERENT café. Offer no explanation." }
    ]
  },
  {
    group: 4,
    id: "presentation",
    prompt: "You've got a work presentation due at 9am tomorrow. It's 6pm.",
    options: [
      { letter: "A", text: "You finished the slides days ago. Now you're lying awake rehearsing the presentation over and over." },
      { letter: "B", text: "Slides were mapped out days ago. You're just executing now. Done and dusted by 8." },
      { letter: "C", text: "Open the file. Look at it. Close the file. Decide it's a 7am-you problem." },
      { letter: "D", text: "Check whether anyone else on the team is drowning and needs your help first" },
      { letter: "E", text: "Start it at 11pm, full confidence, questionable snack, somehow nail it" }
    ]
  },
  {
    group: 4,
    id: "saturday-list",
    prompt: "It's Saturday morning and you've written a to-do list of eight things.",
    options: [
      { letter: "A", text: "Spend twenty minutes paralysed over which one to do first. It's somehow lunchtime now." },
      { letter: "B", text: "Reorder the whole list by priority and estimated time before touching a single thing" },
      { letter: "C", text: "Write the list. Feel deeply productive. Lie back down." },
      { letter: "D", text: "Start with the one someone else asked you to do. Your own seven can wait." },
      { letter: "E", text: "Do number six first because it looked fun. Forget the list exists by lunch." }
    ]
  },

  // ── Group 5: Life Happens ───────────────────────────────────
  {
    group: 5,
    id: "dinner-disaster",
    prompt: "You cooked dinner for friends and it has come out genuinely terrible.",
    options: [
      { letter: "A", text: "Already imagining the debrief they'll have in the car on the way home. You can hear every word of it." },
      { letter: "B", text: "Diagnose it calmly. Seasoning rescue, or full start-from-scratch situation?" },
      { letter: "C", text: "Plate it with total confidence and quietly hope nobody comments" },
      { letter: "D", text: "Already on your feet offering to make everyone something else, anything else, before they've picked up a fork" },
      { letter: "E", text: "Serve it anyway, narrate the disaster like a cooking show, everyone's crying laughing" }
    ]
  },
  {
    group: 5,
    id: "story-correction",
    prompt: "You're telling a story at dinner and a friend jumps in to correct a detail.",
    options: [
      { letter: "A", text: "You already KNEW that detail was off. You've been cringing about it since the words left your mouth." },
      { letter: "B", text: "Take the correction on board and quietly update the story for next time" },
      { letter: "C", text: "Shrug. ‘Close enough.’ Carry on." },
      { letter: "D", text: "Thank them like they just rescued you from total public humiliation" },
      { letter: "E", text: "Double down on the wrong version. It's funnier and you've committed now." }
    ]
  },
  {
    group: 5,
    id: "missed-appointment",
    prompt: "You glance at your phone and realise you missed an appointment an hour ago.",
    options: [
      { letter: "A", text: "Rehearsing the apology call ten times before you dial, then rewording it while it rings." },
      { letter: "B", text: "Check if you can still make it and work out the next available slot" },
      { letter: "C", text: "Reschedule it. Or don't. We'll see how you feel." },
      { letter: "D", text: "Call to apologise, rebook your appointment, and turn up with a box of chocolates for the whole front desk" },
      { letter: "E", text: "Stroll in anyway like you have no idea you were late in the first place" }
    ]
  },
  {
    group: 5,
    id: "queue-wait",
    prompt: "You're in a queue that hasn't moved in ten minutes.",
    options: [
      { letter: "A", text: "Convinced you picked the wrong queue. The other one's moving. It's always the other one." },
      { letter: "B", text: "Quietly counting the items in each line to work out which one is genuinely fastest" },
      { letter: "C", text: "Zone out completely. You're in no rush. If it takes an hour, it takes an hour." },
      { letter: "D", text: "Wave the person behind you ahead because they've only got the one thing" },
      { letter: "E", text: "Put your basket down and leave. Didn't need any of it that badly." }
    ]
  },
  {
    group: 5,
    id: "coffee-wrong",
    prompt: "The barista hands you the wrong coffee order.",
    options: [
      { letter: "A", text: "Wonder if you said it wrong. Did you say it wrong? You're now replaying the exact moment you ordered." },
      { letter: "B", text: "Point it out politely. You ordered an oat flat white, this is full cream. Facts are facts." },
      { letter: "C", text: "Drink it. Can't be bothered with the whole back-and-forth. It's coffee, it's fine." },
      { letter: "D", text: "Drink it so the barista doesn't feel bad, even though it's not what you wanted" },
      { letter: "E", text: "Drink it, decide it's actually better, accidentally order this for the rest of your life" }
    ]
  },
  {
    group: 5,
    id: "coffee-early",
    prompt: "You're 15 minutes early to meet a friend for coffee.",
    options: [
      { letter: "A", text: "Sit there pre-running the conversation, with a backup topic ready in case it goes quiet" },
      { letter: "B", text: "Claim the best table, scan the menu, lock in your order before they arrive" },
      { letter: "C", text: "Sit in the car a while longer. You're not quite ready to go in yet." },
      { letter: "D", text: "Text ‘I'm here, no rush!’ then panic you've made them feel rushed by being early" },
      { letter: "E", text: "Wander into the shop next door, forget you're meeting anyone at all, get a 'you here?' text from your friend" }
    ]
  },
  {
    group: 5,
    id: "on-hold",
    prompt: "You've been on hold for 20 minutes and the same terrible song is looping.",
    options: [
      { letter: "A", text: "Rehearsing exactly what you'll say the second someone picks up. You've now scripted it three different ways." },
      { letter: "B", text: "On speaker, laptop open, clearing emails. Productive hold time, fully optimised." },
      { letter: "C", text: "Hang up. You'll call back later. (You know you won't.)" },
      { letter: "D", text: "Use the dead time to plan something nice for a friend, because if you're stuck here, someone may as well benefit" },
      { letter: "E", text: "Start harmonising with the hold music. It's a duet now. You have a part." }
    ]
  },
  {
    group: 5,
    id: "drawer-photo",
    prompt: "You're cleaning out a drawer and find a photo from years ago.",
    options: [
      { letter: "A", text: "Sit down. Hold it. Disappear into a memory you'd completely forgotten you even had." },
      { letter: "B", text: "Decide whether it's worth keeping based on whether you already have a digital copy" },
      { letter: "C", text: "Put it in a different drawer. That counts as dealing with it." },
      { letter: "D", text: "Resolve to get it framed for them. Add it to the quiet list of nice things you owe people." },
      { letter: "E", text: "Try on whatever's next to it, take a selfie, cleaning fully abandoned" }
    ]
  },
  {
    group: 5,
    id: "gym-late",
    prompt: "You're meeting a friend for a gym class. They're 10 minutes late and it's already started.",
    options: [
      { letter: "A", text: "Standing outside spiralling about where your friend is. Are they okay? Why aren't they answering? You're three texts from calling hospitals." },
      { letter: "B", text: "Text them the schedule link and calculate whether it's still worth going in" },
      { letter: "C", text: "Wait in the car. If they're not here in five minutes, you're going home, a little relieved." },
      { letter: "D", text: "Go in alone so you don't lose the spot, but save them a space just in case" },
      { letter: "E", text: "Walk into the class 10 minutes late like it was the plan all along. Own the entrance." }
    ]
  }
];
